import React, { useState, useEffect } from 'react';
import {
  ComposableMap,
  Geographies,
  Geography,
  Marker,
  Line,
} from 'react-simple-maps';
import { Info, Filter } from 'lucide-react';
import { messagesAPI } from '../services/api';
import axios from 'axios';
import './Map.css';

const geoUrl = 'https://unpkg.com/world-atlas@2/countries-110m.json';

// Real agents will be fetched from API and positioned at AWS regions

// Get color for each AWS region
const getRegionColor = (regionId) => {
  const regionColors = {
    "us-east-1": "#ff6b6b",      // Red
    "us-west-2": "#4ecdc4",      // Teal
    "eu-west-1": "#45b7d1",      // Blue
    "eu-central-1": "#96ceb4",   // Green
    "ap-southeast-1": "#feca57", // Yellow
    "ap-northeast-1": "#ff9ff3", // Pink
    "ca-central-1": "#54a0ff",   // Light Blue
    "sa-east-1": "#5f27cd",      // Purple
    "ap-south-1": "#00d2d3",     // Cyan
    "eu-north-1": "#ff9f43"      // Orange
  };
  return regionColors[regionId] || "#ffffff";
};

// Removed placeholder agents - using real agents from API

// Get region center coordinates for drawing transactions
const getRegionCenter = (regionId) => {
  const AWS_REGIONS = [
    { id: "us-east-1", coordinates: [-77.0369, 38.9072] },
    { id: "us-west-2", coordinates: [-122.6765, 45.5152] },
    { id: "eu-west-1", coordinates: [-6.2603, 53.3498] },
    { id: "eu-central-1", coordinates: [8.6821, 50.1109] },
    { id: "ap-southeast-1", coordinates: [103.8198, 1.3521] },
    { id: "ap-northeast-1", coordinates: [139.6917, 35.6895] },
    { id: "ca-central-1", coordinates: [-75.6919, 45.4215] },
    { id: "sa-east-1", coordinates: [-46.6333, -23.5505] },
    { id: "ap-south-1", coordinates: [72.8777, 19.0760] },
    { id: "eu-north-1", coordinates: [18.0686, 59.3293] }
  ];
  
  const region = AWS_REGIONS.find(r => r.id === regionId);
  return region ? region.coordinates : null;
};

const AgentMarker = ({ agent, isOnline, isActive, onClick, onHover, onLeave }) => {
  //const isOnline = agent.status === 'online';
  
  return (
    <Marker 
      coordinates={agent.coordinates} 
      onClick={() => onClick(agent)}
      onMouseEnter={(e) => onHover(agent, e)}
      onMouseMove={(e) => onHover(agent, e)}
      onMouseLeave={onLeave}
    >
      <g>
        {/* Outer glow ring - larger for online agents */}
        <circle
          r={isOnline ? (isActive ? 8 : 6) : (isActive ? 0 : 0)}
          fill="none"
          stroke={isOnline ? agent.color : "rgba(255, 255, 255, 0.3)"}
          strokeWidth={0.5}
          opacity={isOnline ? 0.6 : 0.3}
          className={isOnline ? "marker-pulse-online" : "marker-pulse"}
        />
        {/* Main marker - bigger and brighter for online */}
        <circle
          r={isOnline ? (isActive ? 3 : 2.2) : (isActive ? 0 : 0)}
          fill={agent.color}
          stroke="rgba(255, 255, 255, 0.5)"
          strokeWidth={0.5}
          className={isOnline ? "marker-glow-online" : "marker-glow"}
          style={{ cursor: 'pointer' }}
          opacity={isOnline ? 1 : 0.6}
        />
        {/* Inner core */}
        <circle
          r={isOnline ? (isActive ? 1.2 : 0.9) : (isActive ? 0 : 0)}
          fill="rgba(255, 255, 255, 0.4)"
          opacity={isOnline ? 0.8 : 0.5}
        />
      </g>
    </Marker>
  );
};

const TransactionLine = ({ fromRegion, toRegion, transaction, isActive }) => {
  // Get coordinates from region IDs
  const fromCoords = getRegionCenter(fromRegion);
  const toCoords = getRegionCenter(toRegion);
  
  if (!fromCoords || !toCoords) return null;
  
  // Calculate the path for the line
  const x1 = fromCoords[0];
  const y1 = fromCoords[1];
  const x2 = toCoords[0];
  const y2 = toCoords[1];
  
  // Create a unique ID for the path
  const pathId = `path-${transaction.id}`;
  
  // Determine colors based on direction
  const lineColor = transaction.direction === 'response' ? 
    'rgba(0, 255, 122, 0.8)' : 'rgba(0, 122, 255, 0.8)';
  const packetColor = transaction.direction === 'response' ? 
    '#00FF7A' : '#007AFF';
  
  return (
    <g>
      {/* Define the path */}
      <defs>
        <path
          id={pathId}
          d={`M ${x1} ${y1} L ${x2} ${y2}`}
          fill="none"
        />
      </defs>
      
      {/* Base line */}
      <Line
        from={fromCoords}
        to={toCoords}
        stroke={isActive ? lineColor : "rgba(255, 255, 255, 0.15)"}
        strokeWidth={isActive ? 2 : 0.8}
        strokeLinecap="round"
        className={isActive ? "transaction-line-active" : "transaction-line"}
      >
        {isActive && (
          <animate
            attributeName="opacity"
            values="0;1;1;0"
            dur="4s"
            begin={`${transaction.animationDelay}s`}
          />
        )}
      </Line>
      
      {/* Animated flowing dashes to show direction */}
      {isActive && (
        <>
          <Line
            from={fromCoords}
            to={toCoords}
            stroke={lineColor}
            strokeWidth={3}
            strokeDasharray="8,4"
            strokeLinecap="round"
            opacity={0.7}
          >
            <animate
              attributeName="stroke-dashoffset"
              values="0;-12;-24"
              dur="2s"
              repeatCount="2"
              begin={`${transaction.animationDelay}s`}
            />
            <animate
              attributeName="opacity"
              values="0;0.7;0.7;0"
              dur="4s"
              begin={`${transaction.animationDelay}s`}
            />
          </Line>
          
          {/* Moving particle */}
          <circle
            r={2}
            fill={packetColor}
            stroke="rgba(255, 255, 255, 0.8)"
            strokeWidth={1}
            filter="drop-shadow(0 0 4px currentColor)"
          >
            <animateMotion
              dur="3s"
              repeatCount="1"
              begin={`${transaction.animationDelay}s`}
              rotate="auto"
            >
              <mpath href={`#${pathId}`} />
            </animateMotion>
            <animate
              attributeName="r"
              values="2;3;2"
              dur="0.5s"
              repeatCount="6"
              begin={`${transaction.animationDelay}s`}
            />
            <animate
              attributeName="opacity"
              values="0;1;1;0"
              dur="4s"
              begin={`${transaction.animationDelay}s`}
            />
          </circle>
          
          {/* Directional arrow at the end */}
          <g transform={`translate(${x2}, ${y2})`}>
            <polygon
              points="-4,-2 4,0 -4,2"
              fill={packetColor}
              opacity={0.8}
              transform={`rotate(${Math.atan2(y2 - y1, x2 - x1) * 180 / Math.PI})`}
            >
              <animate
                attributeName="opacity"
                values="0;0;0.8;0.8;0"
                dur="4s"
                begin={`${transaction.animationDelay}s`}
              />
            </polygon>
          </g>
        </>
      )}
    </g>
  );
};

const Map = () => {
  const [selectedAgent, setSelectedAgent] = useState(null);
  //const [selectedRegion, setSelectedRegion] = useState(null);
  const [hoveredAgent, setHoveredAgent] = useState(null);
  const [tooltipPosition, setTooltipPosition] = useState({ x: 0, y: 0 });
  const [activeTransactions, setActiveTransactions] = useState([]);
  const [showTransactionPanel, setShowTransactionPanel] = useState(false);
  const [realTransactions, setRealTransactions] = useState([]);
  const [realAgents, setRealAgents] = useState([]);
  const [agentStats, setAgentStats] = useState({
    total: 0,
    skill: 0,
    persona: 0,
    communication: 0,
    online: 0
  });
  const [lastUpdate, setLastUpdate] = useState(new Date());
  const [regionStats, setRegionStats] = useState({});
  const [showHealthInfo, setShowHealthInfo] = useState(false);
  const [filters, setFilters] = useState({'region':null});

  // Fetch real agent statistics
  useEffect(() => {
    fetchAgentStats();
    
    // Set up polling for real-time stats
    const interval = setInterval(fetchAgentStats, 60000); // Update every minute
    
    return () => clearInterval(interval);
  }, []);

  // Fetch real transactions
  useEffect(() => {
    fetchRealTransactions();
    
    // Set up polling for real-time transactions
    const interval = setInterval(fetchRealTransactions, 5000); // Update every 5 seconds
    
    return () => clearInterval(interval);
  }, []);

  // Update active transactions when real transactions change
  useEffect(() => {
    if (realTransactions.length > 0) {
      // Take the most recent 5 transactions for animation
      const recentTransactions = realTransactions
        .slice(0, 5)
        .map((tx, index) => ({
          ...tx,
          id: tx._id,
          animationDelay: index * 0.5, // Stagger animations
          direction: tx.type === 'a2a_response' ? 'response' : 'forward',
          startTime: Date.now() + (index * 500) // Track when animation starts
        }));
      
      setActiveTransactions(recentTransactions);
      
      // Clear transactions after animations complete (4s + stagger delay + buffer)
      const maxAnimationTime = 4000 + (recentTransactions.length * 500) + 500;
      setTimeout(() => {
        setActiveTransactions([]);
      }, maxAnimationTime);
      
    } else {
      // Clear transactions if no real ones are available
      setActiveTransactions([]);
    }
  }, [realTransactions]);

  const fetchRealTransactions = async () => {
    try {
      // Fetch recent messages with region data
      const response = await messagesAPI.getMessages({ limit: 20 });
      
      // Filter for transactions that have both from_region and to_region
      const transactions = response.filter(msg => 
        msg.from_region && 
        msg.to_region && 
        msg.from_region !== msg.to_region &&
        // Ensure these are real region IDs
        ['us-east-1', 'us-west-2', 'eu-west-1', 'eu-central-1', 
         'ap-southeast-1', 'ap-northeast-1', 'ca-central-1', 
         'sa-east-1', 'ap-south-1', 'eu-north-1'].includes(msg.from_region) &&
        ['us-east-1', 'us-west-2', 'eu-west-1', 'eu-central-1', 
         'ap-southeast-1', 'ap-northeast-1', 'ca-central-1', 
         'sa-east-1', 'ap-south-1', 'eu-north-1'].includes(msg.to_region)
      );
      
      console.log('Fetched real transactions:', transactions.length);
      setRealTransactions(transactions);
    } catch (error) {
      console.error('Failed to fetch real transactions:', error);
    }
  };

  const fetchAgentStats = async () => {
    try {
      const response = await axios.get('/api/agents?type=all&limit=10000');
      
      let agents;
      if (response.data.agents) {
        agents = response.data.agents;
      } else {
        agents = response.data;
      }

      // Map real agents to AWS region coordinates
      const AWS_REGIONS = [
        { id: "us-east-1", coordinates: [-77.0369, 38.9072] },
        { id: "us-west-2", coordinates: [-122.6765, 45.5152] },
        { id: "eu-west-1", coordinates: [-6.2603, 53.3498] },
        { id: "eu-central-1", coordinates: [8.6821, 50.1109] },
        { id: "ap-southeast-1", coordinates: [103.8198, 1.3521] },
        { id: "ap-northeast-1", coordinates: [139.6917, 35.6895] },
        { id: "ca-central-1", coordinates: [-75.6919, 45.4215] },
        { id: "sa-east-1", coordinates: [-46.6333, -23.5505] },
        { id: "ap-south-1", coordinates: [72.8777, 19.0760] },
        { id: "eu-north-1", coordinates: [18.0686, 59.3293] }
      ];

      // Distribute real agents across AWS regions
      const agentsWithCoordinates = agents.map((agent, index) => {
        const regionIndex = index % AWS_REGIONS.length;
        const region = AWS_REGIONS[regionIndex];
        
        // Add slight variation around region center
        const lat = region.coordinates[1] + (Math.random() - 0.5) * 1;
        const lng = region.coordinates[0] + (Math.random() - 0.5) * 1;
        
        return {
          ...agent,
          coordinates: [lng, lat],
          regionId: region.id,
          color: getRegionColor(region.id)
        };
      });

      setRealAgents(agentsWithCoordinates);

      const skillCount = agents.filter(a => a.category === 'skill').length;
      const personaCount = agents.filter(a => a.category === 'persona').length;
      const commCount = agents.filter(a => a.category === 'communication').length;
      const onlineCount = agents.filter(a => a.status === 'online').length;
///
      // Calculate agents per region
      const regionCounts = {};
      AWS_REGIONS.forEach(region => {
        const count = agentsWithCoordinates.filter(a => a.regionId === region.id).length;
        const onlineInRegion = agentsWithCoordinates.filter(a => a.regionId === region.id && a.status === 'online').length;
        regionCounts[region.id] = { total: count, online: onlineInRegion };
      });
      setRegionStats(regionCounts);

      setAgentStats({
        total: agents.length,
        skill: skillCount,
        persona: personaCount,
        communication: commCount,
        online: onlineCount
      });
      setLastUpdate(new Date());
    } catch (error) {
      console.error('Failed to fetch agent stats for map:', error);
    }
  };

  const handleAgentClick = (agent) => {
    setSelectedAgent(agent);
    setShowTransactionPanel(true);
  };

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({
      ...prev,
      [key]: value
    }));
  };

  const handleAgentHover = (agent, event) => {
    setHoveredAgent(agent);
    if (event) {
      setTooltipPosition({ x: event.clientX, y: event.clientY });
    }
  };

  const handleAgentLeave = () => {
    setHoveredAgent(null);
  };

  // const handleTransactionClick = (transaction) => {
  //   setActiveTransaction(transaction);
  //   setShowTransactionPanel(true);
  // };

  // Get the first active transaction for the panel (if any)
  const displayTransaction = activeTransactions[0];

  return (
    <div className="map-page">
      <div className="map-header">
        <h1>NANDA Global Network</h1>
        <div className="map-stats">
          <div className="stat-item">
            <div className="stat-value">{agentStats.total.toLocaleString()}</div>
            <div className="stat-label">Total Agents</div>
          </div>
          <div className="stat-item">
            <div className="stat-value" style={{ color: 'var(--success)' }}>{((filters.region === 'eu-north-1' ? 0: regionStats['eu-north-1']?.online || 0) + (filters.region === 'us-east-1' ? 0: regionStats['us-east-1']?.online || 0) + (filters.region === 'us-west-2' ? 0: regionStats['us-west-2']?.online || 0) + (filters.region === 'eu-west-1' ? 0: regionStats['eu-west-1']?.online || 0) + (filters.region === 'eu-central-1' ? 0: regionStats['eu-central-1']?.online || 0) + (filters.region === 'ap-southeast-1' ? 0: regionStats['ap-southeast-1']?.online || 0) + (filters.region === 'ap-northeast-1' ? 0: regionStats['ap-northeast-1']?.online || 0) + (filters.region === 'ap-south-1' ? 0: regionStats['ap-south-1']?.online || 0) + (filters.region === 'sa-east-1' ? 0: regionStats['sa-east-1']?.online || 0) + (filters.region === 'ca-central-1' ? 0: regionStats['ca-central-1']?.online || 0)).toLocaleString()}</div>
            <div className="stat-label">Online Agents</div>
          </div>
          <div className="stat-item" style={{ position: 'relative' }}>
            <div className="stat-value" style={{ 
              color: agentStats.total > 0 && ((agentStats.online / agentStats.total) * 100) > 50 ? 'var(--success)' : 'var(--warning)'
            }}>
              {agentStats.total > 0 ? ((((filters.region === 'eu-north-1' ? 0: regionStats['eu-north-1']?.online || 0) + (filters.region === 'us-east-1' ? 0: regionStats['us-east-1']?.online || 0) + (filters.region === 'us-west-2' ? 0: regionStats['us-west-2']?.online || 0) + (filters.region === 'eu-west-1' ? 0: regionStats['eu-west-1']?.online || 0) + (filters.region === 'eu-central-1' ? 0: regionStats['eu-central-1']?.online || 0) + (filters.region === 'ap-southeast-1' ? 0: regionStats['ap-southeast-1']?.online || 0) + (filters.region === 'ap-northeast-1' ? 0: regionStats['ap-northeast-1']?.online || 0) + (filters.region === 'ap-south-1' ? 0: regionStats['ap-south-1']?.online || 0) + (filters.region === 'sa-east-1' ? 0: regionStats['sa-east-1']?.online || 0) + (filters.region === 'ca-central-1' ? 0: regionStats['ca-central-1']?.online || 0)) / agentStats.total) * 100).toFixed(1) : '0.0'}%
            </div>
            <div className="stat-label" style={{ display: 'flex', alignItems: 'center', gap: '6px', justifyContent: 'flex-end' }}>
              Network Health
              <button
                onMouseEnter={() => setShowHealthInfo(true)}
                onMouseLeave={() => setShowHealthInfo(false)}
                style={{
                  background: 'rgba(255, 255, 255, 0.1)',
                  border: '1px solid rgba(255, 255, 255, 0.2)',
                  borderRadius: '50%',
                  width: '16px',
                  height: '16px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: 'help',
                  padding: 0
                }}
              >
                <Info size={10} style={{ color: 'rgba(255, 255, 255, 0.7)' }} />
              </button>
            </div>
            {showHealthInfo && (
              <div style={{
                position: 'absolute',
                top: '100%',
                right: 0,
                marginTop: '8px',
                background: 'rgba(20, 20, 20, 0.98)',
                border: '1px solid rgba(255, 255, 255, 0.3)',
                borderRadius: '8px',
                padding: '12px 16px',
                minWidth: '280px',
                fontSize: '0.75rem',
                color: 'var(--text-secondary)',
                lineHeight: '1.5',
                zIndex: 10000,
                boxShadow: '0 8px 24px rgba(0, 0, 0, 0.8)',
                backdropFilter: 'blur(20px)',
                textAlign: 'left',
                pointerEvents: 'none'
              }}>
                <div style={{ fontWeight: '600', color: 'var(--text-primary)', marginBottom: '8px', fontSize: '0.8125rem' }}>
                  Network Health Calculation
                </div>
                <div style={{ marginBottom: '6px' }}>
                  <strong style={{ color: 'var(--text-primary)' }}>Formula:</strong> (Online Agents / Total Agents) × 100
                </div>
                <div style={{ marginBottom: '6px' }}>
                  <strong style={{ color: 'var(--text-primary)' }}>Current:</strong> ({agentStats.online} / {agentStats.total}) × 100 = {agentStats.total > 0 ? ((agentStats.online / agentStats.total) * 100).toFixed(1) : '0.0'}%
                </div>
                <div style={{ paddingTop: '8px', borderTop: '1px solid rgba(255, 255, 255, 0.1)', fontSize: '0.7rem' }}>
                  <div style={{ color: 'var(--success)' }}>✓ &gt;50% = Healthy (Green)</div>
                  <div style={{ color: 'var(--warning)' }}>⚠ &lt;50% = Warning (Yellow)</div>
                </div>
              </div>
            )}
          </div>
          <div className="stat-item">
            <div className="stat-value" style={{ fontSize: '0.875rem', color: 'var(--text-secondary)' }}>
              {activeTransactions.length}
            </div>
            <div className="stat-label">Active Messages</div>
          </div>
        </div>
        <div style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', marginTop: '8px', textAlign: 'right' }}>
          Last updated: {Math.floor((new Date() - lastUpdate) / 1000)}s ago
        </div>
      </div>

     {/* Filters */}
        <div style={{ 
          display: 'flex', 
          gap: '12px', 
          marginBottom: '20px',
          padding: '16px',
          background: 'var(--bg-secondary)',
          borderRadius: '8px',
          border: '1px solid var(--border)'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <Filter size={16} style={{ color: 'var(--text-secondary)' }} />
            <span style={{ fontSize: '0.875rem', color: 'var(--text-secondary)' }}>Simulate cloud outage in:</span>
          </div>
          
          <select 
            className="input"
            style={{ minWidth: '120px', fontSize: '0.875rem', padding: '6px 12px' }}
            value={filters.region || ''}
            onChange={(e) => handleFilterChange('region', e.target.value || null)}
          >
	    <option value="None">None</option>
            <option value="us-east-1">us-east-1</option>
            <option value="us-west-2">us-west-2</option>
            <option value="eu-west-1">eu-west-1</option>
            <option value="eu-central-1">eu-central-1</option>
            <option value="ap-southeast-1">ap-southeast-1</option>
            <option value="ap-northeast-1">ap-northeast-1</option>
	    <option value="ap-south-1">ap-south-1</option>
	    <option value="sa-east-1">sa-east-1</option>
	    <option value="ca-central-1">ca-central-1</option>
	    <option value="eu-north-1">eu-north-1</option>
          </select>
</div>

      <div className="map-container">
          <ComposableMap
            projection="geoEqualEarth"
            projectionConfig={{ center: [0, -5], scale: 180 }}
            style={{ width: '100%', height: '100%' }}
          >
          {/* Ocean/Water background */}
          <defs>
            <radialGradient id="oceanGradient" cx="50%" cy="50%" r="50%">
              <stop offset="0%" stopColor="rgba(20, 20, 20, 0.8)" />
              <stop offset="100%" stopColor="rgba(10, 10, 10, 0.9)" />
            </radialGradient>
          </defs>
          <rect 
            x={-500} 
            y={-300} 
            width={1000} 
            height={600} 
            fill="url(#oceanGradient)" 
          />
          <Geographies geography={geoUrl}>
            {({ geographies }) =>
              geographies.map((geo) => (
                <Geography
                  key={geo.rsmKey}
                  geography={geo}
                  style={{
                    default: { 
                      fill: '#3a3a3a', 
                      stroke: 'rgba(255, 255, 255, 0.3)',
                      strokeWidth: 0.6,
                      outline: 'none' 
                    },
                    hover: { 
                      fill: '#454545', 
                      outline: 'none' 
                    },
                    pressed: { 
                      fill: '#505050', 
                      outline: 'none' 
                    },
                  }}
                />
              ))
            }
          </Geographies>

          {/* Real agent markers */}
          {realAgents.map((agent, index) => (
            <AgentMarker
              key={agent.id}
              agent={agent}
	      isOnline ={agent.status === 'online' && filters.region !== agent.regionId}
              isActive={selectedAgent?.id === agent.id || 
                       activeTransactions.some(t => t.from === index || t.to === index)}
              onClick={handleAgentClick}
              onHover={handleAgentHover}
              onLeave={handleAgentLeave}
            />
          ))}

          {/* Active transaction lines */}
          {activeTransactions.map((transaction, index) => {
            // Skip if no region data
            if (!transaction.from_region || !transaction.to_region) {
              return null;
            }
            
            return (
              <TransactionLine
                key={`${transaction.id}-${index}`}
                fromRegion={transaction.from_region}
                toRegion={transaction.to_region}
                transaction={transaction}
                isActive={true}
              />
            );
          })}
        </ComposableMap>

        {/* Legend */}
        <div className="map-legend">
          <div className="legend-title">AWS Regions</div>
          <div className="legend-items" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}>
            <div className="legend-item">
              <div className="legend-marker" style={{ backgroundColor: '#ff6b6b' }}></div>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: '11px', fontWeight: '500' }}>us-east-1</div>
                <div style={{ fontSize: '9px', color: 'rgba(255,255,255,0.5)' }}>
                  {filters.region === 'us-east-1' ? 0: regionStats['us-east-1']?.online || 0}/{regionStats['us-east-1']?.total || 0}
                </div>
              </div>
            </div>
            <div className="legend-item">
              <div className="legend-marker" style={{ backgroundColor: '#4ecdc4' }}></div>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: '11px', fontWeight: '500' }}>us-west-2</div>
                <div style={{ fontSize: '9px', color: 'rgba(255,255,255,0.5)' }}>
                  {filters.region === 'us-west-2' ? 0: regionStats['us-west-2']?.online || 0}/{regionStats['us-west-2']?.total || 0}
                </div>
              </div>
            </div>
            <div className="legend-item">
              <div className="legend-marker" style={{ backgroundColor: '#45b7d1' }}></div>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: '11px', fontWeight: '500' }}>eu-west-1</div>
                <div style={{ fontSize: '9px', color: 'rgba(255,255,255,0.5)' }}>
                  {filters.region === 'eu-west-1' ? 0: regionStats['eu-west-1']?.online || 0}/{regionStats['eu-west-1']?.total || 0}
                </div>
              </div>
            </div>
            <div className="legend-item">
              <div className="legend-marker" style={{ backgroundColor: '#96ceb4' }}></div>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: '11px', fontWeight: '500' }}>eu-central-1</div>
                <div style={{ fontSize: '9px', color: 'rgba(255,255,255,0.5)' }}>
                  {filters.region === 'eu-central-1' ? 0: regionStats['eu-central-1']?.online || 0}/{regionStats['eu-central-1']?.total || 0}
                </div>
              </div>
            </div>
            <div className="legend-item">
              <div className="legend-marker" style={{ backgroundColor: '#feca57' }}></div>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: '11px', fontWeight: '500' }}>ap-southeast-1</div>
                <div style={{ fontSize: '9px', color: 'rgba(255,255,255,0.5)' }}>
                  {filters.region === 'ap-southeast-1' ? 0: regionStats['ap-southeast-1']?.online || 0}/{regionStats['ap-southeast-1']?.total || 0}
                </div>
              </div>
            </div>
            <div className="legend-item">
              <div className="legend-marker" style={{ backgroundColor: '#ff9ff3' }}></div>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: '11px', fontWeight: '500' }}>ap-northeast-1</div>
                <div style={{ fontSize: '9px', color: 'rgba(255,255,255,0.5)' }}>
                  {filters.region === 'ap-northeast-1' ? 0: regionStats['ap-northeast-1']?.online || 0}/{regionStats['ap-northeast-1']?.total || 0}
                </div>
              </div>
            </div>
	    <div className="legend-item">
              <div className="legend-marker" style={{ backgroundColor: '#00d2d3' }}></div>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: '11px', fontWeight: '500' }}>ap-south-1</div>
                <div style={{ fontSize: '9px', color: 'rgba(255,255,255,0.5)' }}>
                  {filters.region === 'ap-south-1' ? 0: regionStats['ap-south-1']?.online || 0}/{regionStats['ap-south-1']?.total || 0}
                </div>
              </div>
            </div>
	    <div className="legend-item">
              <div className="legend-marker" style={{ backgroundColor: '#5f27cd' }}></div>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: '11px', fontWeight: '500' }}>sa-east-1</div>
                <div style={{ fontSize: '9px', color: 'rgba(255,255,255,0.5)' }}>
                  {filters.region === 'sa-east-1' ? 0: regionStats['sa-east-1']?.online || 0}/{regionStats['sa-east-1']?.total || 0}
                </div>
              </div>
            </div>
	    <div className="legend-item">
              <div className="legend-marker" style={{ backgroundColor: '#5f27cd' }}></div>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: '11px', fontWeight: '500' }}>ca-central-1</div>
                <div style={{ fontSize: '9px', color: 'rgba(255,255,255,0.5)' }}>
                  {filters.region === 'ca-central-1' ? 0: regionStats['ca-central-1']?.online || 0}/{regionStats['ca-central-1']?.total || 0}
                </div>
              </div>
            </div>
	    <div className="legend-item">
              <div className="legend-marker" style={{ backgroundColor: '#ff9f43' }}></div>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: '11px', fontWeight: '500' }}>eu-north-1</div>
                <div style={{ fontSize: '9px', color: 'rgba(255,255,255,0.5)' }}>
                  {filters.region === 'eu-north-1' ? 0: regionStats['eu-north-1']?.online || 0}/{regionStats['eu-north-1']?.total || 0}
                </div>
              </div>
            </div>
          </div>
          
          <div className="legend-title" style={{ marginTop: '16px' }}>Agent Status</div>
          <div className="legend-items">
            <div className="legend-item">
              <div className="legend-marker marker-pulse-online" style={{ backgroundColor: '#00FF7A' }}></div>
              <span>Online (pulsing)</span>
            </div>
            <div className="legend-item">
              <div className="legend-marker" style={{ backgroundColor: '#ff4444' }}></div>
              <span>Offline</span>
            </div>
          </div>
          
          <div className="legend-title" style={{ marginTop: '16px' }}>Message Flow</div>
          <div className="legend-items">
            <div className="legend-item">
              <div className="legend-marker" style={{ backgroundColor: '#007AFF' }}></div>
              <span>Request →</span>
            </div>
            <div className="legend-item">
              <div className="legend-marker" style={{ backgroundColor: '#00FF7A' }}></div>
              <span>← Response</span>
            </div>
          </div>
        </div>
      </div>

      {/* Hover Tooltip */}
      {hoveredAgent && (
        <div 
          className="agent-tooltip"
          style={{
            position: 'fixed',
            left: `${tooltipPosition.x + 15}px`,
            top: `${tooltipPosition.y + 15}px`,
            background: 'rgba(20, 20, 20, 0.95)',
            border: '1px solid rgba(255, 255, 255, 0.2)',
            borderRadius: '8px',
            padding: '12px 16px',
            pointerEvents: 'none',
            zIndex: 1000,
            minWidth: '200px',
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.5)',
            backdropFilter: 'blur(10px)'
          }}
        >
          <div style={{ fontSize: '0.875rem', fontWeight: '600', color: 'var(--text-primary)', marginBottom: '8px' }}>
            {hoveredAgent.name}
          </div>
          <div style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', marginBottom: '4px' }}>
            <span style={{ color: hoveredAgent.status === 'online' ? 'var(--success)' : 'var(--error)' }}>●</span>
            {' '}{hoveredAgent.status === 'online' ? 'Online' : 'Offline'}
          </div>
          <div style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', marginBottom: '4px' }}>
            Type: {hoveredAgent.category || 'N/A'}
          </div>
          <div style={{ fontSize: '0.75rem', color: 'var(--text-secondary)' }}>
            Region: {hoveredAgent.regionId}
          </div>
          <div style={{ fontSize: '0.7rem', color: 'var(--text-secondary)', marginTop: '8px', paddingTop: '8px', borderTop: '1px solid rgba(255, 255, 255, 0.1)' }}>
            Click for details
          </div>
        </div>
      )}

      {/* Transaction Panel */}
      {showTransactionPanel && (
        <div className="transaction-panel">
          <div className="panel-header">
            <div className="panel-title">
              {selectedAgent ? `Agent: ${selectedAgent.name}` : 'Live Transaction'}
            </div>
            <button 
              className="panel-close"
              onClick={() => {
                setShowTransactionPanel(false);
                setSelectedAgent(null);
              }}
            >
              ×
            </button>
          </div>
          
          <div className="panel-content">
            {selectedAgent ? (
              <div className="agent-details">
                <div className="detail-row">
                  <span className="detail-label">Agent:</span>
                  <span className="detail-value">{selectedAgent.name}</span>
                </div>
                <div className="detail-row">
                  <span className="detail-label">AWS Region:</span>
                  <span className="detail-value">{selectedAgent.region} ({selectedAgent.regionName})</span>
                </div>
                <div className="detail-row">
                  <span className="detail-label">Specialty:</span>
                  <span className="detail-value">{selectedAgent.specialty}</span>
                </div>
                <div className="detail-row">
                  <span className="detail-label">Type:</span>
                  <span className="detail-value">{selectedAgent.type}</span>
                </div>
                <div className="detail-row">
                  <span className="detail-label">Status:</span>
                  <span className="detail-value status-online">Online</span>
                </div>
                <div className="detail-row">
                  <span className="detail-label">Coordinates:</span>
                  <span className="detail-value">
                    {selectedAgent.coordinates[1].toFixed(2)}°, {selectedAgent.coordinates[0].toFixed(2)}°
                  </span>
                </div>
              </div>
            ) : (
              <div className="transaction-details">
                <div className="detail-row">
                  <span className="detail-label">From Agent:</span>
                  <span className="detail-value">{displayTransaction?.from_agent || 'N/A'}</span>
                </div>
                <div className="detail-row">
                  <span className="detail-label">To Agent:</span>
                  <span className="detail-value">{displayTransaction?.to_agent || 'N/A'}</span>
                </div>
                <div className="detail-row">
                  <span className="detail-label">From Region:</span>
                  <span className="detail-value">{displayTransaction?.from_region || 'N/A'}</span>
                </div>
                <div className="detail-row">
                  <span className="detail-label">To Region:</span>
                  <span className="detail-value">{displayTransaction?.to_region || 'N/A'}</span>
                </div>
                <div className="detail-row">
                  <span className="detail-label">Type:</span>
                  <span className="detail-value">{displayTransaction?.type || 'N/A'}</span>
                </div>
                <div className="detail-row">
                  <span className="detail-label">Timestamp:</span>
                  <span className="detail-value">
                    {displayTransaction?.timestamp ? new Date(displayTransaction.timestamp).toLocaleTimeString() : 'N/A'}
                  </span>
                </div>
                <div className="detail-row">
                  <span className="detail-label">Active Transactions:</span>
                  <span className="detail-value">{activeTransactions.length}</span>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default Map;
